// DS EDIT 5 — Multi-workspace RAG scope service.
// Reads/writes the thread-level scope and enumerates the user's accessible
// workspaces. Scope WRITES go through the set_thread_rag_scope RPC, which
// re-validates workspace membership server-side (fail-closed, all-or-nothing)
// — the client-side validation here is a fast-fail UX mirror, never the gate.

import { supabase } from "@/lib/supabase";
import { workspacesService } from "@/services/workspacesService";
import {
  normalizeRagScope,
  parseStoredScope,
  validateRagScope,
} from "@/lib/ragScopePure";

export interface AccessibleWorkspace {
  id: string;
  name: string;
  role: string;
}

class RagScopeService {
  /** Workspaces the current user may include in a RAG scope (membership-derived). */
  async getAccessibleWorkspaces(): Promise<AccessibleWorkspace[]> {
    const workspaces = await workspacesService.getWorkspaces();
    return workspaces.map((w) => ({
      id: String(w.id),
      name: w.name || "Untitled Workspace",
      role: String((w as { role?: string }).role || "member"),
    }));
  }

  /** The thread's persisted scope, or null for single-workspace behavior. */
  async getThreadScope(threadId: string): Promise<string[] | null> {
    const { data, error } = await supabase
      .from("chat-threads")
      .select("rag_workspace_ids")
      .eq("id", threadId)
      .single();
    if (error || !data) return null;
    return parseStoredScope((data as { rag_workspace_ids?: unknown }).rag_workspace_ids);
  }

  /**
   * Set (or clear with null/[]) the thread's RAG scope.
   * Server-side RPC enforces ownership + membership; throws on rejection.
   */
  async setThreadScope(threadId: string, workspaceIds: string[] | null): Promise<string[] | null> {
    const ids = workspaceIds ? normalizeRagScope(workspaceIds) : [];

    if (ids.length > 0) {
      const memberships = await this.getAccessibleWorkspaces();
      const check = validateRagScope(ids, memberships.map((m) => m.id));
      if (!check.ok) {
        throw new Error(check.error);
      }
    }

    const { data, error } = await supabase.rpc("set_thread_rag_scope", {
      p_thread_id: threadId,
      p_workspace_ids: ids.length > 0 ? ids : null,
    });
    if (error) {
      throw new Error(`Failed to set RAG scope: ${error.message}`);
    }
    return parseStoredScope(data);
  }
}

export const ragScopeService = new RagScopeService();
